import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Inventory.jsx. No Edit action in
// this list (matching source) — field edits happen inline on
// InventoryDetail.tsx; `current_quantity` itself is never directly editable
// anywhere (InventoryItemController::validated() excludes it — only the
// dedicated "Record a movement" flow can change stock level).
interface InventoryItemRow {
  id: number;
  name: string;
  category: string | null;
  unit: string | null;
  current_quantity: number;
  reorder_point: number | null;
  facility: { name: string } | null;
}

export function Inventory() {
  const { lists } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);

  const filters = useMemo(
    () => ({
      search: search || undefined,
      category: category || undefined,
      low_stock: lowStockOnly || undefined,
    }),
    [search, category, lowStockOnly]
  );
  const { data, loading, error, setPage, reload: load } = usePaginatedList<InventoryItemRow>('/inventory-items', filters);

  const remove = async (item: InventoryItemRow) => {
    if (!(await confirmDelete(`Delete "${item.name}"?`))) return;
    try {
      await api.delete(`/inventory-items/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<InventoryItemRow>(
      api,
      '/inventory-items',
      { search: search || undefined, category: category || undefined, low_stock: lowStockOnly || undefined },
      [
        { label: 'Item', value: (r) => r.name },
        { label: 'Category', value: (r) => (r.category ? labelFor(lists, 'inventory_category', r.category) : '') },
        { label: 'Quantity', value: (r) => r.current_quantity },
        { label: 'Unit', value: (r) => (r.unit ? labelFor(lists, 'inventory_unit', r.unit) : '') },
        { label: 'Reorder Point', value: (r) => r.reorder_point ?? '' },
        { label: 'Facility', value: (r) => r.facility?.name ?? '' },
      ],
      'inventory.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Inventory</h1>
        <Link className="btn btn-primary" to="/resources/inventory/new">
          + Add Inventory
        </Link>
      </header>
      <p className="muted">
        Consumables and stock materials — purchased, stored, issued, and replenished during normal operation.
      </p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search item name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {listOptions(lists, 'inventory_category').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <label className="checkbox-inline">
          <input type="checkbox" checked={lowStockOnly} onChange={(e) => setLowStockOnly(e.target.checked)} />
          Low stock only
        </label>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Category</th>
                <th className="right">Quantity</th>
                <th>Facility</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.name}</strong>
                    </td>
                    <td>{item.category ? labelFor(lists, 'inventory_category', item.category) : '—'}</td>
                    <td className="right">
                      {Number(item.current_quantity)} {item.unit ? labelFor(lists, 'inventory_unit', item.unit) : ''}
                      {item.reorder_point !== null && Number(item.current_quantity) <= Number(item.reorder_point) && (
                        <div>
                          <span className="badge badge-urgent">Low stock</span>
                        </div>
                      )}
                    </td>
                    <td>{item.facility?.name ?? '—'}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/resources/inventory/${item.id}`} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="muted center">
                    No inventory items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
